import { Inject, Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Observable, interval } from 'rxjs';
import { map, share, switchMap, takeWhile, tap } from 'rxjs/operators';

import {
  BulkCSVUpdateJob,
  BulkCSVUpdateMappings,
  BulkCSVUpdateProvider,
  BulkCSVUpdateStatus,
  CreateCSVJobRequest,
} from 'models';

import {
  BulkCSVUpdateDialogComponent,
  BulkCSVUpdateDialogResult,
} from '../components/bulk-csv-update-dialog/bulk-csv-update-dialog.component';

import { BULK_CSV_UPDATE_PROVIDER } from '../common/tokens';

/** Bulk CSV update dialog service. */
@Injectable({
  providedIn: 'root',
})
export class BulkCSVUpdateService {
  constructor(
    private dialog: MatDialog,
    @Inject(BULK_CSV_UPDATE_PROVIDER)
    private bulkCsvUpdateProvider: BulkCSVUpdateProvider
  ) {}

  /**
   * Start a Bulk CSV Update.
   *
   * @param databaseId Database ID.
   *
   * @throws An error saying 'Cancelled' if the bulk csv update dialog is closed without a result.
   * @returns An observable job ID.
   */
  openBulkCSVUpdateDialog(databaseId: number): Observable<string> {
    const dialog = this.dialog.open(BulkCSVUpdateDialogComponent, {
      disableClose: true,
      width: '700px',
      data: {
        databaseId,
      },
    });
    return dialog.afterClosed().pipe(
      tap((result: BulkCSVUpdateDialogResult) => {
        if (!result) throw new Error('Cancelled');
      }),
      map((result: BulkCSVUpdateDialogResult) => result.jobId)
    );
  }

  /**
   * Cancel a job.
   *
   * @param jobId Job ID.
   * @returns An observable that resolves when the task completes.
   */
  cancelJob(jobId: string): Observable<void> {
    return this.bulkCsvUpdateProvider.cancelJob(jobId);
  }

  /**
   * Delete a job that has been cancelled or completed.
   *
   * @param jobId Job ID.
   * @returns An observable that resolves when the task completes.
   */
  deleteJob(jobId: string): Observable<void> {
    return this.bulkCsvUpdateProvider.deleteJob(jobId);
  }

  /**
   * Downloads the job result.
   *
   * @param jobId  Job Id
   */
  downloadJobResult(jobId: string): Observable<void> {
    return this.bulkCsvUpdateProvider.downloadJobResult(jobId);
  }

  /**
   * Get create job request. Because we are technically getting the create job request.
   *
   * @param databaseId - Database Id.
   * @param columnDelimiter - Column Delimiter.
   * @param stringDelimiter - String Delimiter
   * @param containsColumnHeaders - Whether column headers exist.
   * @param token - Auth Token
   * @returns - Csv job request.
   */
  getCreateJobRequest(
    databaseId: number,
    columnDelimiter: string,
    stringDelimiter: string,
    containsColumnHeaders: boolean,
    token: string
  ): CreateCSVJobRequest {
    return this.bulkCsvUpdateProvider.getCreateJobRequest(
      databaseId,
      columnDelimiter,
      stringDelimiter,
      containsColumnHeaders,
      token
    );
  }

  /**
   * Get job.
   *
   * @param jobId - Job ID.
   * @returns - An observable job.
   */
  getJob(jobId: string): Observable<BulkCSVUpdateJob> {
    return this.bulkCsvUpdateProvider.getJob(jobId);
  }

  /**
   * Get the job column mappings.
   *
   * @param jobId - Job Id.
   * @returns - Observable of the CSV Update Mappings.
   */
  getJobColumnMappings(jobId: string): Observable<BulkCSVUpdateMappings> {
    return this.bulkCsvUpdateProvider.getJobColumnMappings(jobId);
  }

  /**
   * Get the job listener for the job.
   *
   * @param jobId Job ID.
   * @returns An observable listener that checks the job status every few seconds.
   */
  getJobListener = (jobId: string) =>
    interval(5000).pipe(
      switchMap(() => this.getJob(jobId)),
      takeWhile(
        (job) => job.status.started && !job.status.done,
        true // makes takeWhile emit the last value which matched the above condition
      ),
      share()
    );

  /**
   * Gets the job and also saves its updated status to the store.
   *
   * @param jobId Job ID.
   * @returns An observable job.
   */
  getJobStatus(jobId: string): Observable<BulkCSVUpdateStatus> {
    return this.bulkCsvUpdateProvider.getJobStatus(jobId);
  }

  /**
   * Prompt user with bulk update selections.
   *
   */
  promptBulkCSVUpdate(): void {
    this.dialog.open(BulkCSVUpdateDialogComponent, {
      width: '800px',
      disableClose: true,
    });
  }

  /**
   * Start a job.
   *
   * @param jobId Job ID.
   * @param columnMappings Column mappings.
   * @returns An observable job.
   */
  startJob(
    jobId: string,
    columnMappings: BulkCSVUpdateMappings,
    overwriteSystemFields: boolean
  ): Observable<BulkCSVUpdateJob> {
    return this.bulkCsvUpdateProvider
      .startUpdate(jobId, columnMappings, overwriteSystemFields)
      .pipe(switchMap(() => this.getJob(jobId)));
  }
}
